import { Pagination } from '@components/Pagination';
import { color, query, space } from '@design/templateFns';
import { ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

export interface CardGridProps {
  cardsPerPage: number; // 0 means no pagination
  cardArr: ReactNode[];
  cardType?:
    | 'shop'
    | 'article'
    | 'advisor'
    | 'development'
    | 'pressRelease'
    | 'media'
    | 'property'
    | 'shop'; //used for custom styling right now
  pagination?: boolean;
  testIdType?: string;
}

const CardGridWithPagination = ({
  cardsPerPage,
  cardArr,
  cardType,
  testIdType,
  pagination = true,
}: CardGridProps): ReactNode => {
  // Ref to scrollTo
  const scrollRef = useRef<any>(null);

  // Determine number of pages (if cardsPerPage is 0, then we don't want to paginate)
  const numberOfPages = cardsPerPage === 0 ? 1 : Math.ceil(cardArr.length / cardsPerPage);

  // PAGINATION HANDLING
  const [currentPage, handleCurrentPage] = useState(1); // Current page for pagination
  const [currentCards, handleCurrentCards] = useState<ReactNode[]>([]); // Current cards to display

  // Set current cards to display
  useEffect(() => {
    //setting the card length to 0 should result in *all* cards being displayed, no pagination
    if (cardsPerPage === 0) {
      handleCurrentCards(cardArr);
      return;
    }
    const indexOfLastCard = currentPage * cardsPerPage;
    const indexOfFirstCard = indexOfLastCard - cardsPerPage;
    const currentCards = cardArr.slice(indexOfFirstCard, indexOfLastCard);
    handleCurrentCards(currentCards);
  }, [currentPage, cardsPerPage, cardArr]);

  // Handle selecting a page
  const handlePageSelect = (page: number): void => {
    if (scrollRef.current) {
      const offset = scrollRef.current.offsetTop - 240;
      window.scrollTo({
        top: offset,
        behavior: 'smooth',
      });
    }
    handleCurrentPage(page);
  };

  return (
    <Container ref={scrollRef}>
      <StyledCardContainer data-testid={testIdType ? `${testIdType}-styled-card-container` : ''}>
        {currentCards.map((card, ind) => {
          return (
            <CardWrapper
              key={ind}
              data-testid={testIdType ? `${testIdType}-card-wrapper-${ind}` : ''}
            >
              {card}
            </CardWrapper>
          );
        })}
      </StyledCardContainer>
      {numberOfPages > 1 && pagination && (
        <PaginationContainer $cardType={cardType}>
          <Pagination
            currentPage={currentPage}
            totalPages={numberOfPages}
            pageSelected={handlePageSelect}
          />
        </PaginationContainer>
      )}
    </Container>
  );
};

export default CardGridWithPagination;

const Container = styled.div``;

export const StyledCardContainer = styled.div`
  display: grid;
  grid-template-columns: 100%;
  grid-gap: ${space('gutterMobile')};

  @media only screen and (${query.atLeast('tablet')}) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${space('x5')};
  }

  @media (${query.atLeast('desktop')}) {
    grid-template-columns: repeat(3, 1fr);
    grid-gap: ${space('gutter')};
  }
`;

const PaginationContainer = styled.div<{ $cardType?: string }>`
  justify-content: center;
  display: flex;
  margin-top: ${({ $cardType }) => ($cardType === 'pressRelease' ? space('x8') : space('x7'))};

  @media (${query.atLeast('desktop')}) {
    margin-top: ${({ $cardType }) => ($cardType === 'pressRelease' ? space('x10') : space('x8'))};
  }
`;

const CardWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
`;

export const NoResultCard = styled.div`
  color: ${color('gray5')};
  text-align: center;
  margin: ${space('x12')} 0;
`;
